import { Card, CardContent } from '@/components/ui/shadcn/card';
import { useTranslations } from '@/hooks/useTranslations';
import { formatDate } from '@/lib/utils';
import type { ProjectTask } from '@/types';
import { Link } from '@inertiajs/react';
import { CalendarDays, FolderOpen } from 'lucide-react';
import { TaskScheduleBadge, TaskStatusBadge } from './task-badges';

/** The mobile view of one task row — same facts, stacked. */
export function TaskCard({ task }: { task: ProjectTask }) {
    const { t } = useTranslations();
    const projectId = task.project?.id ?? task.project_id;

    const body = (
        <Card className="mb-2 transition-shadow hover:shadow-sm">
            <CardContent className="space-y-2 p-3">
                <div className="flex items-start justify-between gap-2">
                    <h3 className="min-w-0 flex-1 text-sm font-semibold break-words">{task.name}</h3>
                    <TaskStatusBadge status={task.status} className="shrink-0" />
                </div>

                <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
                    <FolderOpen className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">
                        {task.project
                            ? `${task.project.contract_number ?? ''} ${task.project.contract_name ?? ''}`.trim()
                            : t('projects.tables.tasks.no_project', 'No project')}
                    </span>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
                        <CalendarDays className="h-3.5 w-3.5 shrink-0" />
                        <span>
                            {task.start_date ? formatDate(task.start_date) : '—'} — {task.end_date ? formatDate(task.end_date) : '—'}
                        </span>
                    </div>
                    <TaskScheduleBadge task={task} />
                </div>
            </CardContent>
        </Card>
    );

    return projectId ? (
        <Link href={`/projects/${projectId}`} className="touch-target block">
            {body}
        </Link>
    ) : (
        body
    );
}
